<template>
  <div
    :key="localeUpdated"
    :class="{'modalbg':routeName}"
  >
    <VtLoading v-if="showLoading" />
    <nav-bar
      v-if="!isRoutePatientCases && !isRouteViewer"
      ref="navBar"
      :touch-mode="routeName || routeHome || routeGrossingTouchSampleView || routeGrossingTouchPendingView"
      @show-global-locator="$refs.globalLocatorModal.show($event)"
      @incident-added="onIncidentAdded"
      @incident-closed="onIncidentClosed"
    />
    <modal-confirmation
      id="confirmationModalGlobal"
      ref="confirmationModalGlobal"
      :qa-id="qaIdGlobalModalConfirmation"
      :ok-title="okTitleGlobalModalConfirmation"
      :cancel-title="cancelTitleGlobalModalConfirmation"
      :content="messageGlobalModalConfirmation"
      :cancel-on-hide="cancelOnHideGlobalModalConfirmation"
      :checkable="isModalConfirmationCheckable"
      :dont-show-again-checkable-text="dontShowAgainCheckableText"
    />
    <router-view
      ref="routerView"
      :key="labId"
    />
    <VtFooter v-if="hideFooter" />

    <!--Modal Footer -->
    <span v-if="userLoggedIn">
      <modal-wrapper ref="globalLocatorModal">
        <global-locator-modal :touch-mode="routeName || routeHome" />
      </modal-wrapper>
    </span>
  </div>
</template>

<script>
import VtLoading from '@/components/common/VtLoading'
import NavBar from '@/components/NavBar/NavBar'
import VtFooter from '@/components/common/VtFooter'
import GlobalLocatorModal from '@/modules/globalLocatorModule/GlobalLocatorModal'
import ModalConfirmation from '@/components/modals/ModalConfirmation'
import { omit } from 'lodash'

export default {
    name: 'Layout',
    components: { NavBar, VtFooter, VtLoading, GlobalLocatorModal, ModalConfirmation },
    beforeRouteEnter(to, from, next) {
        next(vm => {
            if (to.query.accessToken) {
                const queryWithoutToken = omit(to.query, ['accessToken'])
                let newLocation = {
                    name: to.name,
                    path: to.path,
                    params: queryWithoutToken
                }
                if (to.name === 'viewerModule') {
                    newLocation.query = queryWithoutToken
                }
                vm.$router.replace(newLocation)
            }
        })
    },
    data() {
        return {
            showLoading: false,
            messageGlobalModalConfirmation: '',
            okTitleGlobalModalConfirmation: null,
            cancelTitleGlobalModalConfirmation: null,
            qaIdGlobalModalConfirmation: null,
            cancelOnHideGlobalModalConfirmation: true,
            counterLoading: 0,
            isModalConfirmationCheckable: false,
            modalConfirmationCheckStatus: false,
            dontShowAgainCheckableText: this.$tc('component.modalConfirmation.dontShowAgain')
        }
    },
    computed: {
        hideFooter() {
            return this.$route.name !== 'viewerModule'
        },
        isRoutePatientCases() {
            return this.$route.name === 'patientCases'
        },
        isRouteViewer() {
            return this.$route.name === 'viewerModule'
        },
        isLoading() {
            return this.$store.getters.isLoading
        },
        routeName() {
            return this.$route.name === 'ContentTracking'
        },
        routeHome() {
            return this.$route.name === 'HomeTracking'
        },
        routeGrossingTouchPendingView() {
            return this.$route.name === 'GrossingTouchPendingView'
        },
        routeGrossingTouchSampleView() {
            return this.$route.name === 'GrossingTouchSampleView'
        },
        userLoggedIn() {
            return this.$store.getters.userLoggedIn
        },
        labId() {
            return this.$store.getters.labId
        },
        localeUpdated() {
            return this.$i18n.locale
        }
    },
    watch: {
        labId() {
            if (this.$route.params.id) {
                delete this.$route.params.id
            }
        },
        isLoading(newValue) {
            this.showLoading = newValue
        }
    },
    mounted() {
        this.$eventHub.$on('showConfirmationModal', this.onShowConfirmationModal)
        this.$eventHub.$on('showConfirmationModalCheckable', this.onShowConfirmationModalCheckable)
        this.$eventHub.$on('showGlobalSearchMultipleResult', this.onShowGlobalSearchMultipleResult)
    },
    beforeDestroy() {
        this.$eventHub.$off('showConfirmationModal', this.onShowConfirmationModal)
        this.$eventHub.$off('showConfirmationModalCheckable', this.onShowConfirmationModalCheckable)
        this.$eventHub.$off('showGlobalSearchMultipleResult', this.onShowGlobalSearchMultipleResult)
    },
    methods: {
        async onShowConfirmationModal(question) {
            const response =
                await this.askAndGetAnswerGlobal(
                    question.question,
                    question.okTitle,
                    question.cancelTitle,
                    question.qaId,
                    question.cancelOnHide)
            this.$eventHub.$emit('confirmationModalResponse', response)
        },
        async onShowConfirmationModalCheckable(question) {
            const response =
                await this.showConfirmationModalCheckable(
                    question.question,
                    question.okTitle,
                    question.cancelTitle,
                    question.qaId,
                    question.cancelOnHide,
                    question.dontShowAgainCheckableText)
            this.$eventHub.$emit('confirmationModalResponseCheckable', response)
        },
        async onShowGlobalSearchMultipleResult(search) {
            const elementSelected =
                await this.showGlobalSearchMultipleResult(
                    search.text,
                    search.entities)
            this.$eventHub.$emit('globalSearchMultipleResultResponse', elementSelected)
        },
        onIncidentAdded() {
            if (this.$refs.routerView.onIncidentAdded) {
                this.$refs.routerView.onIncidentAdded()
            }
        },
        onIncidentClosed() {
            if (this.$refs.routerView.onIncidentClosed) {
                this.$refs.routerView.onIncidentClosed()
            }
        },
        async askAndGetAnswerGlobal(question, okTitle, cancelTitle, qaId, cancelOnHide) {
            return new Promise((resolve) => {
                this.messageGlobalModalConfirmation = question
                this.okTitleGlobalModalConfirmation = okTitle
                this.cancelTitleGlobalModalConfirmation = cancelTitle
                this.qaIdGlobalModalConfirmation = qaId
                this.cancelOnHideGlobalModalConfirmation = cancelOnHide
                this.isModalConfirmationCheckable = false

                this.$refs.confirmationModalGlobal.ask(
                    () => resolve(true),
                    () => resolve(false))
            })
        },
        async showConfirmationModalCheckable(question, okTitle, cancelTitle, qaId, cancelOnHide, dontShowAgainCheckableText) {
            return new Promise((resolve) => {
                this.messageGlobalModalConfirmation = question
                this.okTitleGlobalModalConfirmation = okTitle
                this.cancelTitleGlobalModalConfirmation = cancelTitle
                this.qaIdGlobalModalConfirmation = qaId
                this.cancelOnHideGlobalModalConfirmation = cancelOnHide
                this.isModalConfirmationCheckable = true
                this.dontShowAgainCheckableText = dontShowAgainCheckableText
                this.$refs.confirmationModalGlobal?.ask(
                    (dontShowAgainCheckStatus) => resolve({ buttonAction: true, dontShowAgainCheckStatus }),
                    (dontShowAgainCheckStatus) => resolve({ buttonAction: false, dontShowAgainCheckStatus }))
            })
        },
        async showGlobalSearchMultipleResult(searchText, entities) {
            return new Promise((resolve) => {
                this.$refs.globalLocatorModal.show([
                    this.$tc('global.searcher'),
                    searchText,
                    entities,
                    (entitySelected) => resolve(entitySelected),
                    () => resolve(false)])
            })
        }
    }
}
</script>
